import React, { createContext, useState, useEffect } from "react";
import { isTokenExpired } from "../Utils/checkToken";
import api from "../Utils/api";
import { offlineDraftsEnabled } from "../offline/draftStore";

const Context = createContext();

function AuthProvider({ children }) {
    const BASE_URL = process.env.REACT_APP_BASE_URL;

    const [authenticated, setAuthenticated] = useState(false);
    const [offlineMode, setOfflineMode] = useState(false);
    const [authLoading, setAuthLoading] = useState(true);
    const [municipio, setMunicipio] = useState(
        localStorage.getItem("x-user-municipio") || "Balsas"
    );

    useEffect(() => {
        const token = localStorage.getItem("x-access-token");

        if (!token || isTokenExpired()) {
            setAuthenticated(false);
            setAuthLoading(false);
            return;
        }

        let active = true;
        api.get("/auth/session")
            .then(({ data }) => {
                if (!active) return;
                localStorage.setItem("x-user-id", data.user.userId);
                localStorage.setItem("x-user-mat-id", data.user.userMatriculaId || "");
                localStorage.setItem("x-user-tipo", data.user.userTipo);
                localStorage.setItem("x-role-key", data.user.roleKey || "");
                localStorage.setItem("x-permissions", JSON.stringify(data.user.permissions || []));
                setOfflineMode(false);
                setAuthenticated(true);
            })
            .catch((error) => {
                if (active) {
                    const canUseCachedSession = !error.response && offlineDraftsEnabled &&
                        Boolean(localStorage.getItem("x-user-id")) &&
                        Boolean(localStorage.getItem("x-permissions"));
                    if (canUseCachedSession) {
                        setOfflineMode(true);
                        setAuthenticated(true);
                    } else {
                        localStorage.removeItem("x-permissions");
                        localStorage.removeItem("x-role-key");
                        setOfflineMode(false);
                        setAuthenticated(false);
                    }
                }
            })
            .finally(() => {
                if (active) setAuthLoading(false);
            });

        const refreshSessionWhenOnline = () => {
            if (!localStorage.getItem("x-access-token") || isTokenExpired()) return;
            api.get("/auth/session").then(({ data }) => {
                localStorage.setItem("x-user-id", data.user.userId);
                localStorage.setItem("x-user-mat-id", data.user.userMatriculaId || "");
                localStorage.setItem("x-user-tipo", data.user.userTipo || "");
                localStorage.setItem("x-role-key", data.user.roleKey || "");
                localStorage.setItem("x-permissions", JSON.stringify(data.user.permissions || []));
                setOfflineMode(false);
                setAuthenticated(true);
            }).catch(() => undefined);
        };
        window.addEventListener("online", refreshSessionWhenOnline);
        return () => {
            active = false;
            window.removeEventListener("online", refreshSessionWhenOnline);
        };
    }, []);

    useEffect(() => {
        if (!authenticated) return undefined;
        const token = localStorage.getItem("x-access-token");
        try {
            const expiration = JSON.parse(atob(token.split(".")[1])).exp * 1000;
            const timeout = Math.max(0, Math.min(expiration - Date.now(), 2147483000));
            const timer = window.setTimeout(() => {
                localStorage.removeItem("x-access-token");
                localStorage.removeItem("x-permissions");
                localStorage.removeItem("x-role-key");
                setOfflineMode(false);
                setAuthenticated(false);
            }, timeout);
            return () => window.clearTimeout(timer);
        } catch {
            setOfflineMode(false);
            setAuthenticated(false);
            return undefined;
        }
    }, [authenticated]);

    return (
        <Context.Provider value={{ authenticated, setAuthenticated, authLoading, offlineMode, setOfflineMode, BASE_URL, municipio, setMunicipio }}>
            {children}
        </Context.Provider>
    );
}

export { Context, AuthProvider };
